<template>
  <div class="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg relative transition-shadow duration-300 p-6 space-y-6">
    <div class="flex items-start justify-between">
      <div class="w-full">
        <button
            @click="redirectToIndex"
            class="inline-flex items-center text-gray-600 hover:text-gray-800 mb-6"
        >
          <Icon name="arrow_back_ios" class="w-5 h-5 text-xl text-zinc-800" />
          {{ __('Back to Challenge') }}
        </button>

        <h1 class="text-3xl font-medium text-zinc-800 mb-6">
          {{ data.challenge.title }}
        </h1>

        <div class="flex w-full sm:hidden mb-6">
          <button
              class="flex-1 text-center py-2 font-medium border transition-colors duration-150 rounded-l-lg"
              :class="mobileTab === 'overview'
              ? 'bg-secondary text-white border-transparent'
              : 'bg-white text-zinc-600 border-zinc-300'"
              @click="mobileTab = 'overview'"
          >
            <span class="text-sm">{{ __('Overview') }}</span>
          </button>

          <button
              class="flex-1 text-center py-2 font-medium border transition-colors duration-150 rounded-r-lg"
              :class="mobileTab === 'answers'
              ? 'bg-secondary text-white border-transparent'
              : 'bg-white text-zinc-600 border-zinc-300'"
              @click="mobileTab = 'answers'"
          >
            <span class="text-sm">{{ __('Answers') }}</span>
          </button>
        </div>

        <div v-if="mobileTab === 'overview'" class="sm:hidden space-y-6">
          <div class="flex flex-row items-center justify-center w-full text-center px-3 py-2 text-gray-600 rounded-xl border-2 border-gray-200 text-2xl font-medium gap-2">
            <span class="text-3xl font-bold text-zinc-800">{{ displayScore }}</span>
            <span>{{ __('Points') }}</span>
          </div>
          <div>
            <span class="text-sm text-gray-600">{{ __('Submission Date:') }}</span>
            <span class="text-sm text-gray-800 font-medium ml-2">{{ formattedSubmissionDate }}</span>
          </div>

          <div class="flex flex-wrap gap-2">
            <span class="px-3 py-1 text-zinc-800 rounded-full border-2 border-gray-200 text-sm font-medium">
              {{ data.challenge.type_label }}
            </span>
            <span v-if="data.challenge.category" class="px-3 py-1 text-zinc-800 rounded-full border-2 border-gray-200 text-sm font-medium">
              {{ data.challenge.category }}
            </span>
            <span class="inline-flex items-center px-3 py-1 text-zinc-800 rounded-full border-2 border-gray-200 text-sm font-medium">
              <img
                  class="rounded-sm object-cover w-5 h-5 mr-1"
                  :src="companyLogo"
                  :alt="__('Company logo image')"
              />
              {{ data.challenge.company.name }}
            </span>
          </div>

          <div v-if="data.challenge.description">
            <h2 class="text-lg font-medium text-zinc-800 mb-3">{{ __('General Feedback') }}</h2>
            <p class="text-gray-600 text-base leading-relaxed">
              <span v-if="!showFullFeedback">{{ truncatedDescription }}</span>
              <span v-else>{{ data.challenge.description }}</span>
              <button
                  v-if="data.challenge.description.length > 400"
                  @click="showFullFeedback = !showFullFeedback"
                  class="text-primary hover:text-primary/70 ml-1 underline text-base"
              >
                {{ showFullFeedback ? __('see less') : __('see more') }}
              </button>
            </p>
          </div>
        </div>

        <div class="hidden sm:block space-y-6">
          <div class="mb-4">
            <span class="text-sm text-gray-600 mb-4">{{ __('Submission Date:') }}</span>
            <span class="text-sm text-gray-800 font-medium">{{ formattedSubmissionDate }}</span>
          </div>

          <div class="flex flex-wrap gap-2">
            <span class="px-3 py-1 text-zinc-800 rounded-full border-2 border-gray-200 text-sm font-medium">
              {{ data.challenge.type_label }}
            </span>
            <span v-if="data.challenge.category" class="px-3 py-1 text-zinc-800 rounded-full border-2 border-gray-200 text-sm font-medium">
              {{ data.challenge.category }}
            </span>
            <span class="inline-flex items-center px-3 py-1 text-zinc-800 rounded-full border-2 border-gray-200 text-sm font-medium">
              <img
                  class="rounded-sm object-cover w-5 h-5 mr-1"
                  :src="companyLogo"
                  :alt="__('Company logo image')"
              />
              {{ data.challenge.company.name }}
            </span>
          </div>

          <div v-if="data.challenge.description">
            <h2 class="text-lg font-medium text-zinc-800 mb-3">{{ __('General Feedback') }}</h2>
            <p class="text-gray-600 text-base leading-relaxed">
              <span v-if="!showFullFeedback">{{ truncatedDescription }}</span>
              <span v-else>{{ data.challenge.description }}</span>
              <button
                  v-if="data.challenge.description.length > 400"
                  @click="showFullFeedback = !showFullFeedback"
                  class="text-primary hover:text-primary/70 ml-1 underline text-base"
              >
                {{ showFullFeedback ? __('see less') : __('see more') }}
              </button>
            </p>
          </div>
        </div>
      </div>

      <div v-if="isDesktop || isTablet" class="flex items-center gap-4 ml-6">
        <div class="flex flex-col justify-center items-center w-36 h-36 rounded-xl border-2 border-zinc-200">
          <div class="text-6xl font-bold text-zinc-800">{{ displayScore }}</div>
          <div class="pl-4 text-2xl text-gray-600">{{ __('Points') }}</div>
        </div>
      </div>
    </div>
  </div>

  <div v-if="mobileTab === 'answers' || !isMobile" :class="{ 'sm:block': true, 'hidden': mobileTab !== 'answers' && isMobile }" class="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 p-6 space-y-6 mt-4">
    <div class="mb-4">
      <ChallengeProgressBar
          :current="currentQuestionIndex + 1"
          :total="totalQuestions"
      />
    </div>

    <QuestionAnswersCard
        v-if="currentQuestion"
        :question="currentQuestion"
        :question-number="currentQuestionIndex + 1"
        :is-review-mode="true"
        :is-first-question="currentQuestionIndex === 0"
        :is-last-question="currentQuestionIndex === totalQuestions - 1"
        @next="nextQuestion"
        @previous="previousQuestion"
        @done="redirectToIndex"
    />
  </div>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import { __ } from '@/helpers';
import { Icon } from '@/components/block/icon';
import QuestionAnswersCard from "@/pages/candidate/challenges/components/QuestionAnswersCard.vue";
import ChallengeProgressBar from "@/pages/candidate/challenges/components/ChallengeProgressBar.vue";

const props = defineProps({
  data: {
    type: Object,
    required: true
  }
});

const currentQuestionIndex = ref(0);
const showFullFeedback = ref(false);
const mobileTab = ref('overview');

const totalQuestions = computed(() => props.data.questions.length);
const currentQuestion = computed(() => props.data.questions[currentQuestionIndex.value]);
const windowWidth = ref(window.innerWidth);

const isMobile = computed(() => windowWidth.value < 640);
const isTablet = computed(() => windowWidth.value >= 640 && windowWidth.value < 1024);
const isDesktop = computed(() => windowWidth.value >= 1024);

const isFirstQuestion = computed(() => currentQuestionIndex.value === 0);
const isLastQuestion = computed(() => currentQuestionIndex.value === totalQuestions.value - 1);

const displayScore = computed(() => {
  if (props.data.attempt.needs_review) {
    return '---';
  }
  return props.data.attempt.score;
});

const truncatedDescription = computed(() => {
  const desc = props.data.challenge.description || '';
  return desc.length > 400 ? desc.substring(0, 400) + '...' : desc;
});

const companyLogo = computed(() => {
  const logo = props.data.challenge.company.logo;
  return logo ? `/uploads/${logo}` : '/images/logo_placeholder.png';
});

const formattedSubmissionDate = computed(() => {
  const d = new Date(props.data.attempt.completed_at);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
});

const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});

const nextQuestion = () => {
  if (currentQuestionIndex.value < totalQuestions.value - 1) {
    currentQuestionIndex.value++;
  }
};

const previousQuestion = () => {
  if (currentQuestionIndex.value > 0) {
    currentQuestionIndex.value--;
  }
};

const redirectToIndex = () => {
  window.location.href = route('challenges.index');
};
</script>